if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const productVariantId = formData.get('id');
        const selectedAddOns = this.getSelectedAddOns();

        try {
          const response =
            selectedAddOns.length > 0
              ? await this.submitProductWithAddOns(formData, selectedAddOns)
              : await this.submitProduct(formData);

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error)
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId,
              cartData: response,
            });
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(response);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        } catch {
          this.handleErrorMessage('We could not add this item. Please try again.');
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.submitButton.removeAttribute('aria-busy');
          if (!this.error) {
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.disabled = false;
          }
          this.querySelector('.loading__spinner').classList.add('hidden');
        }
      }

      getSelectedAddOns() {
        const productInfo = this.closest('product-info');
        const addOnsElement =
          productInfo?.querySelector('product-add-ons') ||
          document.querySelector(`product-add-ons[data-product-form-id="${this.form.id}"]`) ||
          document.querySelector('product-add-ons');

        if (!addOnsElement) return [];

        if (typeof addOnsElement.getSelectedAddOns === 'function') {
          const selectedAddOns = addOnsElement.getSelectedAddOns();
          if (selectedAddOns.length > 0) return selectedAddOns;
        }

        return this.getSelectedAddOnsFromInputs(addOnsElement);
      }

      getSelectedAddOnsFromInputs(addOnsElement) {
        const parentTitle = addOnsElement.dataset.parentTitle || document.querySelector('.product__title h1')?.textContent || '';

        return Array.from(addOnsElement.querySelectorAll('.product-add-ons__item'))
          .map((row) => {
            const quantityMode = row.dataset.quantityMode || 'match_parent';
            const quantityInput = row.querySelector('[data-add-on-qty]');
            const quantity = this.getQuantityFromInput(quantityInput);

            if (quantityInput?.disabled || quantity < 1) return null;

            return {
              variantId: Number(row.dataset.addOnVariantId),
              title: row.dataset.addOnTitle || '',
              type: row.dataset.addOnType || 'extra',
              quantityMode,
              quantity,
              price: Number(row.dataset.addOnPrice || 0),
              parentTitle,
            };
          })
          .filter((addOn) => addOn?.variantId && addOn.quantity > 0);
      }

      getQuantityFromInput(input) {
        const value = Number(input?.value || 0);
        return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
      }

      async submitProduct(formData) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        return this.fetchCartAdd(routes.cart_add_url, config);
      }

      async submitProductWithAddOns(formData, selectedAddOns) {
        const addOnGroupId = `add-ons-${formData.get('id')}-${Date.now()}`;
        formData.append('properties[_add_on_group]', addOnGroupId);

        const mainResponse = await this.submitProduct(formData);
        if (mainResponse.status || selectedAddOns.length === 0) return mainResponse;

        const addOnsBody = {
          items: selectedAddOns.map((addOn) => ({
            id: addOn.variantId,
            quantity: addOn.quantity,
            properties: {
              'Add-on for': addOn.parentTitle,
              _add_on_parent: addOnGroupId,
              _add_on_type: addOn.type,
              _add_on_quantity_mode: addOn.quantityMode,
            },
          })),
        };

        if (this.cart) {
          addOnsBody.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          addOnsBody.sections_url = window.location.pathname;
        }

        const addOnsConfig = fetchConfig('json');
        addOnsConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        addOnsConfig.body = JSON.stringify(addOnsBody);

        const addOnsResponse = await this.fetchCartAdd(this.getCartAddJsonUrl(), addOnsConfig);
        addOnsResponse.key = mainResponse.key || addOnsResponse.key || addOnsResponse.items?.[0]?.key;

        return addOnsResponse;
      }

      async fetchCartAdd(url, config) {
        const response = await fetch(url, config);
        return response.json();
      }

      getCartAddJsonUrl() {
        if (window.Shopify?.routes?.root) return `${window.Shopify.routes.root}cart/add.js`;
        if (routes.cart_add_url.endsWith('.js')) return routes.cart_add_url;

        return `${routes.cart_add_url}.js`;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
